import { ReactComponent as CloseIcon } from 'static/icons/close.svg';
import styled, { keyframes } from 'styled-components';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

export const Container = styled.div`
  background-color: #fff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  width: auto;
  height: fit-content;
  padding: 12px 14px 12px 12px;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: flex-start;
  gap: 10px;
  overflow: hidden;
  z-index: 3;
  position: relative;
  animation: ${slideIn} 0.3s ease-out;
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
  box-shadow: 0 4px 8px rgba(15, 23, 42, 0.1);
  backdrop-filter: blur(14px);
  background-color: rgba(255, 255, 255, 0.96);

  &[data-size='small'] {
    width: min(340px, calc(100vw - 24px));
    padding: 10px 12px 10px 10px;
    gap: 8px;
  }

  &[data-size='medium'] {
    width: min(480px, calc(100vw - 24px));
    padding: 12px 14px 12px 12px;
  }

  &[data-size='large'] {
    width: min(500px, calc(100vw - 24px));
    padding: 14px 16px 14px 14px;
  }

  &[data-compact='true'] {
    align-items: center;
  }

  &[data-dismissing='true'] {
    animation: ${fadeOut} 0.3s ease-out forwards;
  }

  &:hover {
    box-shadow: 0 6px 8px rgba(15, 23, 42, 0.14);
    transform: translateY(-1px);
  }
`;

export const Body = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: flex-start;
  gap: 10px;
`;

export const ContentSection = styled.div`
  color: ${(props) => props.theme.color.pickledBluewood};
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
  padding-right: 22px;

  ${Container}[data-size='small'] & {
    font-size: 12px;
    gap: 3px;
  }

  ${Container}[data-size='medium'] & {
    font-size: 13px;
  }

  ${Container}[data-size='large'] & {
    font-size: 14px;
  }

  ${Container}[data-compact='true'] & {
    gap: 0;
  }
`;

export const Message = styled.div`
  color: ${(props) => props.theme.color.pickledBluewood};
  line-height: 1.45;
  overflow-wrap: break-word;
  word-break: normal;
  text-wrap: pretty;

  span {
    font-weight: 400;
    display: block;
    width: 100%;
    white-space: normal;
    overflow-wrap: break-word;
    word-break: normal;
  }

  p {
    margin: 0;
  }
`;

export const Bage = styled.div`
  width: 4px;
  align-self: stretch;
  flex-shrink: 0;
  border-radius: 2px;
  margin: 2px 0;
  background-color: ${({ appearance, theme }) => {
    switch (appearance) {
      case 'error':
        return theme.color.bittersweet;
      case 'success':
        return theme.color.brightTurquoise;
      case 'warning':
        return theme.color.koromiko;
      case 'info':
        return theme.color.bittersweet;
      default:
        return theme.color.brightTurquoise;
    }
  }};
  box-shadow: 0 0 0 1px
    ${({ appearance, theme }) => {
      switch (appearance) {
        case 'error':
          return `${theme.color.bittersweet}1f`;
        case 'success':
          return `${theme.color.brightTurquoise}1f`;
        case 'warning':
          return `${theme.color.koromiko}2e`;
        case 'info':
          return '#3B82F61f';
        default:
          return `${theme.color.brightTurquoise}1f`;
      }
    }};
`;

export const StyledClose = styled(CloseIcon)`
  user-select: none;
  inset: 0px;
  margin: auto;
  color: ${({ theme }) => theme.color.pickledBluewood};
  transition: transform 0.2s ease, color 0.2s ease;

  ${Container}[data-size="small"] & {
    width: 14px;
    height: 14px;
  }

  ${Container}[data-size="medium"] & {
    width: 16px;
    height: 16px;
  }

  ${Container}[data-size="large"] & {
    width: 18px;
    height: 18px;
  }

  path {
    transition: stroke 0.2s ease;
  }
`;

export const StyledImg = styled.img`
  border-radius: 10px;
  object-fit: cover;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);

  ${Container}[data-size="small"] & {
    width: 24px;
    height: 24px;
    margin-top: 1px;
  }

  ${Container}[data-size="medium"] & {
    width: 32px;
    height: 32px;
    margin-top: 1px;
  }

  ${Container}[data-size="large"] & {
    width: 40px;
    height: 40px;
    margin-top: 2px;
  }
`;

export const CloseBtnWrapper = styled.div`
  padding: 4px;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 8px;
  right: 8px;
  position: absolute;
  z-index: 2;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(15, 23, 42, 0.06);

    ${StyledClose} {
      transform: scale(1.1);
      color: ${({ theme }) => theme.color.pickledBluewood};
    }
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.color.brightTurquoise}66;
  }
`;

export const Title = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.color.pickledBluewood};
  letter-spacing: -0.01em;
  line-height: 1.35;
  padding-right: 4px;

  ${Container}[data-size="small"] & {
    font-size: 13px;
  }

  ${Container}[data-size="medium"] & {
    font-size: 14px;
  }

  ${Container}[data-size="large"] & {
    font-size: 16px;
  }
`;

export const ToastOverlayLink = styled.a`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

export const CtaWrapper = styled.div`
  margin-top: 6px;
  position: relative;
  z-index: 2;
`;

export const CtaLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 30px;
  padding: 6px 14px;
  background: linear-gradient(135deg, #00696b, #00a4a6);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  border-radius: 8px;
  text-decoration: none;
  white-space: nowrap;
  box-shadow: 0 1px 4px rgba(0, 105, 107, 0.2);
  transition: opacity 0.2s ease, box-shadow 0.2s ease;

  ${Container}[data-size='large'] & {
    min-height: 34px;
    padding: 7px 16px;
    font-size: 13px;
  }

  &:hover {
    opacity: 0.92;
    box-shadow: 0 2px 8px rgba(0, 105, 107, 0.28);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 164, 166, 0.45);
  }
`;

export const Subscription = styled.div`
  color: ${({ theme }) => theme.color.pickledBluewood};
  opacity: 0.85;
  letter-spacing: -0.01em;

  ${Container}[data-size="small"] & {
    font-size: 11px;
  }

  ${Container}[data-size="medium"] & {
    font-size: 12px;
  }

  ${Container}[data-size="large"] & {
    font-size: 13px;
  }
`;
